<template>
  <div class="index py-5">
    <div class="d-flex align-center">
      <span class="d-flex flex-row page-title">{{$t("intranet.title.page.quiz.list")}}</span>
      <v-spacer></v-spacer>
      <AveineQuizImport :APIbaseURL="APIbaseURL" @imported="fetch()" />
      <v-btn depressed color="primary" to="/authenticated/quizzes/create">
        {{$t("intranet.homeContent.button.createQuiz")}}
      </v-btn>
    </div>

    <AveineQuizReminderSettings />

    <div class="searchable-list-container">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
      >
        <template v-slot:item.day="{ item }">{{ formatDay(item.day) }}</template>
        <template v-slot:item.theme="{ item }">{{ translated(item.theme) }}</template>
        <template v-slot:item.question="{ item }">{{ translated(item.question) }}</template>
        <template v-slot:item.published="{ item }">
          <v-chip small :color="item.published ? 'success' : 'grey lighten-1'">
            {{ item.published ? $t('intranet.homeContent.published') : $t('intranet.homeContent.draft') }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon :to="`/authenticated/quizzes/update/${item.id}`">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon color="error" @click="askDelete(item)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <v-dialog v-model="deleteDialog" max-width="480">
      <v-card>
        <v-card-title>{{$t('intranet.homeContent.quiz.deleteTitle')}}</v-card-title>
        <v-card-text>{{$t('intranet.homeContent.quiz.deleteConfirmation')}}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">{{$t('intranet.referential.button.cancel')}}</v-btn>
          <v-btn color="error" text @click="confirmDelete()">{{$t('intranet.referential.button.delete')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Quiz from '@/store/models/quiz'
import AveineQuizImport from '@/components/AveineQuizImport.vue'
import AveineQuizReminderSettings from '@/components/AveineQuizReminderSettings.vue'
import { errorHandler } from 'aveine-toolbox'

export default {
  name: 'Index',
  components: { AveineQuizImport, AveineQuizReminderSettings },
  data () {
    return {
      items: [],
      total: 0,
      loading: false,
      deleteDialog: false,
      itemToDelete: null,
      options: { page: 1, itemsPerPage: 25 },
      APIbaseURL: process.env.VUE_APP_API_HOST
    }
  },
  computed: {
    headers () {
      return [
        { text: this.$t('intranet.homeContent.quiz.day'), value: 'day', sortable: false, width: 220 },
        { text: this.$t('intranet.homeContent.quiz.theme'), value: 'theme', sortable: false },
        { text: this.$t('intranet.homeContent.quiz.question'), value: 'question', sortable: false },
        { text: this.$t('intranet.homeContent.attributes.published'), value: 'published', sortable: false, width: 140 },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 120 }
      ]
    }
  },
  watch: {
    options: { handler () { this.fetch() }, deep: true }
  },
  methods: {
    /**
     * Repli sur l'anglais puis sur la premiere valeur non vide : un quizz
     * traduit dans une seule langue reste lisible dans la liste au lieu
     * d'afficher une case vide.
     */
    translated (field) {
      const value = field || {}
      return value[this.$i18n.locale] || value.en || Object.values(value).find(v => v) || '—'
    },

    /** « lun. 5 oct. 2026 », sans passer par un fuseau. */
    formatDay (value) {
      if (!value) return '—'
      const [year, month, date] = value.slice(0, 10).split('-').map(Number)
      return new Date(year, month - 1, date, 12)
        .toLocaleDateString(this.$i18n.locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    },

    async fetch () {
      this.loading = true
      try {
        const response = await Quiz.api().get(
          `quizzes?lang=${this.$i18n.locale}`,
          {
            baseURL: this.APIbaseURL,
            params: {
              // Les plus lointains d'abord : le planning a venir en tete.
              sort: '-day',
              'page[number]': this.options.page,
              'page[size]': this.options.itemsPerPage
            }
          }
        )
        this.items = response.entities ? (response.entities.quizzes || []) : []
        this.total = response.response.data.meta ? response.response.data.meta.total : this.items.length
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.loading = false
      }
    },

    askDelete (item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },

    async confirmDelete () {
      try {
        // Les reponses suivent : `quiz_answers.quiz_id` est ON DELETE CASCADE.
        await Quiz.api().delete(
          `quizzes/${this.itemToDelete.id}`,
          { baseURL: this.APIbaseURL, save: false }
        )
        this.deleteDialog = false
        this.itemToDelete = null
        await this.fetch()
      } catch (errors) {
        this.deleteDialog = false
        errorHandler(errors, this)
      }
    }
  }
}
</script>
